import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollFadeTabs } from '@/components/ui/ScrollFadeTabs';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export { TabsContent } from '@/components/ui/tabs';

interface TabConfig<T extends string> {
  key: T;
  label: string;
  badge?: number;
  testId?: string;
}

interface DetailTabsProps<T extends string> {
  tabs: TabConfig<T>[];
  defaultTab: T;
  children: React.ReactNode;
  className?: string;
}

function getHashTab<T extends string>(validKeys: T[], defaultTab: T): T {
  const hash = window.location.hash.slice(1);
  if (hash && validKeys.includes(hash as T)) {
    return hash as T;
  }
  return defaultTab;
}

export function DetailTabs<T extends string>({
  tabs,
  defaultTab,
  children,
  className,
}: DetailTabsProps<T>) {
  const validKeys = useMemo(() => tabs.map((t) => t.key), [tabs]);
  const [activeTab, setActiveTab] = useState<T>(() => getHashTab(validKeys, defaultTab));

  const handleChange = useCallback((value: string) => {
    const tab = value as T;
    setActiveTab(tab);
    if (tab === defaultTab) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } else {
      window.history.replaceState(null, '', `#${tab}`);
    }
  }, [defaultTab]);

  useEffect(() => {
    function onHashChange() {
      setActiveTab(getHashTab(validKeys, defaultTab));
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [validKeys, defaultTab]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleChange}
      className={cn('w-full', className)}
    >
      <ScrollFadeTabs activeValue={activeTab}>
        <TabsList
          className={cn(
            'w-full justify-start bg-transparent border-b border-border rounded-none h-auto p-0',
          )}
        >
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.key}
              value={tab.key}
              data-testid={tab.testId}
              className={cn(
                'relative rounded-none border-b-2 border-transparent min-h-[44px] px-4 py-2.5 text-sm font-medium',
                'text-muted-foreground hover:text-foreground transition-colors',
                'data-[state=active]:border-primary data-[state=active]:text-foreground',
                'data-[state=active]:shadow-none',
                'whitespace-nowrap',
              )}
            >
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1.5 h-5 min-w-5 px-1.5 py-0 text-[10px] justify-center"
                >
                  {tab.badge}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </ScrollFadeTabs>
      {children}
    </Tabs>
  );
}
